/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Vladislav Urbánek <urbanek.vlada@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import connectToChild from 'penpal/lib/connectToChild';

// TODO #295 Zrevidovat odchytávání chyb.
// TODO #295 Lepší napojení child?
// - Zatím všude volání `connection.promise` pro přístup k child.
// - Rozdělit aktuální třídu na dvě,
//   přičemž v konstruktoru jednoho bude child a u druhého iframe apod.
//   Jak ale pak zpracovat `connectToParent`, kde je nutné navázat metody?
// TODO Upravit přístup k době přehrávání dle HTML médií:
// - Výchozí je hodnota pageRate, která se zadá v konfiguraci.
// - Po načtení počtu stran v PDF se vypočte duration = pagesCount / pageRate.
//   Vypočtená hodnota se sdělí v události durationchange rodiči a ovladači.
//   Předchozí read-only hodnota duration byla NaN.
// - Pokud ovladač nyní spustí přehrávání použije se výchozí rychlost.
// - Ovladač má ale možnost na základě známé a požadované duration upravit hodnotu playbackRate
//   a tím ovlivnit fyzickou délku přehrávání PDF.
// - https://developer.mozilla.org/en-US/docs/Web/Guide/Audio_and_video_delivery/WebAudio_playbackRate_explained
// - https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/duration
// - https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/playbackRate
// - https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/durationchange_event
// - https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/defaultPlaybackRate
// - https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/ratechange_event
// TODO #295 Problém s vertikálním centrováním stran.
// - https://github.com/mozilla/pdf.js/issues/10906
// TODO #295 Problém se zobrazením více stran.
// - https://github.com/mozilla/pdf.js/issues/10907
// TODO #295 Používat sync metody místo async? Vyžadovalo by přesun logiky sem.
// - Jak třeba okamžitě zastavit přehrávání, když se operace provede async?
/**
 * Rodič při komunikaci s přehrávačem PDF.
 * Ovládá iframe a vyváří v něm potomka,
 * který ovládá PDF přehrávač běžící uvnitř iframe.
 *
 * Rozhraní se snaží dodržovat konvence zavedené v
 * [HTMLVideoElement](https://developer.mozilla.org/en-US/docs/Web/API/HTMLVideoElement)
 * a [HTMLMediaElement](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement).
 * V základní logice je přehrávání videa a PDF stejné, frame videa odpovídá straně PDF.
 * Přesto je zde několik rozdílů:
 *
 * - Místo `addEventListener` a `removeEventListener`
 *   se používá `on` a `off`.
 * - Nejsou dostupné varianty naslouchání jako `onerror` apod.
 * - Místo ES5 getterů a setterů se používají metody.
 *   Např. místo vlastnosti `src` jsou zde dvě metody `getSrc` a `setSrc`.
 * - Všechny metody jsou asynchronní, protože se předávají přes iframe do jiné aplikace.
 * - Číslování stránek je od 1, ale číslování frame od 0.
 * - U HTML videa je `currentFrame` nedostupný, což vychází z vysoké rychlosti přehrávání
 *   a nutnosti synchronizace s různými displeji.
 *   U PDF existuje `currentPage` s přesným číslem aktuálně zobrazené strany.
 *   Díky tomu lze jednoduše přepnout na požadovanou stranu,
 *   aniž by bylo nutné řešit problémy se zaokrouhlováním času.
 *   Např. PDF s délkou 10s na 3 strany se těžko přepne na druhou stranu,
 *   aniž by problikla první.
 *   Na druhou stranu nelze tímto přístupem přehrávání nastavit přesný okamžik přehrávání.
 *   Např. přesun doprostřed časové osy u PDF se třemi stranami.
 *   Použití tohoto přístupu nijak neomezuje budoucí nasazení `currentTime` vedle `currentPage`.
 * - Narozdíl od videa dovoluje libovolně nastavit `duration`.
 *
 * Pro správnou funkcionalitu PDF prohlížeče na `file://` protokolu
 * je nutné na Chrome použít argument `--allow-file-access-from-files`.
 * Jinak nedojde ke spárování origin u `postMessage` API
 * a také knihovna PDF.js načítá některé soubory za běhu.
 * @implements {Destroyable}
 * @implements {EventBus}
 */
class PdfParent {
  /**
   * Vytvoří instanci.
   * @param {HTMLIFrameElement} iframe Iframe, do kterého se načte potomek s PDF prohlížečem.
   * @param {string} origin Origin, který budou mít zprávy od potomka.
   * @param {number} timeout Maximální doba čekání na navázání komunikace. [ms]
   */
  constructor(iframe, origin, timeout) {
    this.emitter = new EventEmitter();
    // Navázání spojení s aplikací potomka.
    this.connection = connectToChild({
      childOrigin: origin,
      iframe,
      methods: {
        durationchange: this.onDurationchange.bind(this),
        ended: this.onEnded.bind(this),
        error: this.onError.bind(this)
      },
      timeout
    });
    iframe.src = 'pdf-viewer/viewer.html';
  }

  /** @inheritdoc */
  destroy() {
    this.connection.destroy();
  }

  /**
   * Zjistí zdroj PDF.
   * Zdroje:
   * [HTMLMediaElement.src](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/src).
   * @returns {Promise.<string>} URL adresa souboru s PDF.
   */
  getSrc() {
    return new Promise((resolve) =>
      resolve(this.connection.promise.then((child) => child.getSrc()))
    );
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na změnu délky přehrávání PDF.
   * Zdroje:
   * [HTMLMediaElement durationchange](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/durationchange_event).
   * @param {number} duration Délka přehrávání. [s]
   * @private
   * @returns {Promise.<undefined>}
   */
  onDurationchange(duration) {
    return this.emitter.emit('durationchange', duration);
  }

  /**
   * Reakce na dokončení přehrání PDF.
   * @private
   * @returns {undefined}
   */
  onEnded() {
    return this.emitter.emit('ended');
  }

  /**
   * Reakce na chybu z PDF přehrávače.
   * @param {string} message
   * @private
   * @returns {undefined}
   */
  onError(message) {
    const error = new Error(message);
    return this.emitter.emit('error', error);
  }

  /**
   * Pozastaví přehrávání PDF.
   * Zdroje:
   * [HTMLMediaElement.pause](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/pause).
   * @returns {Promise.<undefined>}
   */
  pause() {
    return new Promise((resolve) =>
      resolve(this.connection.promise.then((child) => child.pause()))
    );
  }

  /**
   * Spustí přehrávání PDF, během kterého se v požadovaném intervalu
   * prochází jednotlivými stránkami dokumentu.
   * Zdroje:
   * [HTMLMediaElement.play](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/play).
   * @returns {Promise.<undefined>}
   */
  play() {
    return new Promise((resolve) =>
      resolve(this.connection.promise.then((child) => child.play()))
    );
  }

  /**
   * Nastaví aktuálně zobrazenou stranu v PDF.
   * Zdroje:
   * [HTMLMediaElement.currentTime](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/currentTime).
   * @param {number} page Číslo strany, první je 1.
   * @returns {Promise.<undefined>}
   */
  setCurrentPage(page) {
    return new Promise((resolve) =>
      resolve(
        this.connection.promise.then((child) => child.setCurrentPage(page))
      )
    );
  }

  /**
   * Nastaví rychlost přehrávání PDF.
   * Zdroje:
   * [HTMLMediaElement.playbackRate](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/playbackRate).
   * @param {number} playbackRate Rychlost přehrávání PDF, kde 0 je zastaveno a 1 je výchozí rychlost.
   * @returns {Promise.<undefined>}
   */
  setPlaybackRate(playbackRate) {
    return new Promise((resolve) =>
      resolve(
        this.connection.promise.then((child) =>
          child.setPlaybackRate(playbackRate)
        )
      )
    );
  }

  /**
   * Nastaví zdroj PDF.
   * Zdroje:
   * [HTMLMediaElement.src](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/src).
   * @param {string} src URL adresa souboru s PDF.
   * @returns {Promise.<undefined>}
   */
  setSrc(src) {
    return new Promise((resolve) =>
      resolve(this.connection.promise.then((child) => child.setSrc(src)))
    );
  }
}

/**
 * The `durationchange` event is fired when the `duration` attribute has been updated.
 * Zdroje:
 * [HTMLMediaElement.durationchange](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/durationchange_event).
 * @event PdfParent#durationchange
 * @param {number} duration
 */

/**
 * The `ended` event is fired when playback or streaming has stopped
 * because the end of the media was reached or because no further data is available.
 * Zdroje:
 * [HTMLMediaElement.ended](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/ended_event).
 * @event PdfParent#ended
 */

/**
 * The `error` event is fired when the resource could not be loaded due to an error
 * Zdroje:
 * [HTMLMediaElement.error](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/error_event).
 * @event PdfParent#error
 * @param {string} message
 */

export default PdfParent;
